"use client";

import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Play, Pause, SkipForward, Square, Settings2, MapPin } from "lucide-react";
import type { UseTourRouteReturn } from "./useTourRoute";
import type { Waypoint } from "@/features/routebuilder/types";
import { TourRoutePhase, VehicleStyle } from "./types";
import TourRouteSettings from "./TourRouteSettings";
import { useTheme } from "@/features/theme";

// ── Vehicle SVGs (top-down silhouettes, rotate around center) ─────────────────

const CAR_SVG = `
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32" width="32" height="32">
  <filter id="s"><feDropShadow dx="0" dy="1" stdDeviation="2" flood-color="rgba(0,0,0,0.5)"/></filter>
  <g filter="url(#s)">
    <rect x="8" y="5" width="16" height="22" rx="5" fill="#6366f1"/>
    <rect x="10" y="8" width="12" height="7" rx="2" fill="#a5b4fc" opacity="0.8"/>
    <rect x="10" y="19" width="12" height="5" rx="2" fill="#a5b4fc" opacity="0.6"/>
    <circle cx="10" cy="8"  r="2" fill="#1e1b4b"/>
    <circle cx="22" cy="8"  r="2" fill="#1e1b4b"/>
    <circle cx="10" cy="24" r="2" fill="#1e1b4b"/>
    <circle cx="22" cy="24" r="2" fill="#1e1b4b"/>
  </g>
</svg>`;

const WALK_SVG = `
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32" width="32" height="32">
  <filter id="s"><feDropShadow dx="0" dy="1" stdDeviation="2" flood-color="rgba(0,0,0,0.5)"/></filter>
  <g filter="url(#s)" fill="#6366f1">
    <circle cx="16" cy="7" r="3"/>
    <path d="M13 12 Q16 11 19 12 L20 19 L16 21 L12 19 Z"/>
    <path d="M12 19 L10 27" stroke="#6366f1" stroke-width="2.5" stroke-linecap="round" fill="none"/>
    <path d="M20 19 L22 27" stroke="#6366f1" stroke-width="2.5" stroke-linecap="round" fill="none"/>
  </g>
</svg>`;

const BIKE_SVG = `
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32" width="32" height="32">
  <filter id="s"><feDropShadow dx="0" dy="1" stdDeviation="2" flood-color="rgba(0,0,0,0.5)"/></filter>
  <g filter="url(#s)">
    <circle cx="10" cy="22" r="5" stroke="#6366f1" stroke-width="2.5" fill="none"/>
    <circle cx="22" cy="22" r="5" stroke="#6366f1" stroke-width="2.5" fill="none"/>
    <path d="M10 22 L16 12 L22 22" stroke="#6366f1" stroke-width="2.5" stroke-linecap="round" fill="none"/>
    <path d="M14 12 L18 12" stroke="#6366f1" stroke-width="2" stroke-linecap="round"/>
    <circle cx="16" cy="10" r="2.5" fill="#6366f1"/>
  </g>
</svg>`;

const VEHICLE_SVG: Record<VehicleStyle, string> = {
  car:     CAR_SVG,
  walking: WALK_SVG,
  bike:    BIKE_SVG,
};

// ── Props ─────────────────────────────────────────────────────────────────────

interface TourRoutePlayerProps extends UseTourRouteReturn {
  waypoints:    Waypoint[];
  rawMapRef:    React.RefObject<any>;
  isDark:       boolean;
  onViewingEnd?: () => void;
  onDismiss?:   () => void; // called after dismissing the completion banner
}

// ── Component ─────────────────────────────────────────────────────────────────

// ── Formatters ────────────────────────────────────────────────────────────────

function fmtDist(m: number): string {
  if (m >= 1000) return `${(m / 1000).toFixed(1)} km`;
  return `${Math.round(m)} m`;
}

function fmtTime(sec: number): string {
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  const s = Math.floor(sec % 60);
  if (h > 0) return `${h}h ${m.toString().padStart(2, "0")}m`;
  if (m > 0) return `${m}m ${s.toString().padStart(2, "0")}s`;
  return `${s}s`;
}

// ── Component ─────────────────────────────────────────────────────────────────

export default function TourRoutePlayer({
  phase, pausedFrom, targetWaypointIndex, segmentProgress, carPositionRef,
  routeStats,
  play, pause, resume, skip, stop,
  settings, setSettings,
  waypoints, rawMapRef, isDark: isDarkProp, onViewingEnd, onDismiss,
}: TourRoutePlayerProps) {
  const { isDark: themeIsDark } = useTheme();
  const isDark = isDarkProp ?? themeIsDark;
  const [settingsOpen, setSettingsOpen] = useState(false);
  const carElRef = useRef<HTMLDivElement | null>(null);  // raw DOM div — no MapLibre Marker

  // Auto-pause while the settings menu is open, but only resume on close if
  // *this* is what paused it — a tour the user had already paused manually
  // should stay paused after they close the menu.
  const autoPausedByMenuRef = useRef(false);
  useEffect(() => {
    if (settingsOpen) {
      if (phase === "driving" || phase === "arriving" || phase === "viewing") {
        autoPausedByMenuRef.current = true;
        pause();
      }
    } else if (autoPausedByMenuRef.current) {
      autoPausedByMenuRef.current = false;
      resume();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settingsOpen]);

  // ── Car marker: raw DOM div positioned via map.project() ─────────────────
  // Bypasses MapLibre's Marker system entirely.
  // map.project(lngLat) → pixel coords, then we set a single CSS transform:
  //   translate(x - half, y - half) rotate(bearing)
  // `will-change: transform` keeps the element on the GPU compositor layer.

  // Create / recreate the div when vehicle style changes
  useEffect(() => {
    if (carElRef.current) { carElRef.current.remove(); carElRef.current = null; }

    const el = document.createElement("div");
    el.style.cssText =
      "position:absolute;top:0;left:0;width:32px;height:32px;" +
      "pointer-events:none;will-change:transform;";
    el.innerHTML = VEHICLE_SVG[settings.vehicleStyle];
    carElRef.current = el;

    return () => { el.remove(); carElRef.current = null; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settings.vehicleStyle]);

  // Icon sync via map.on('render', ...) — fires inside the same JS task as the
  // WebGL paint, so map.project() always reflects the exact camera being drawn.
  // This eliminates the 1-frame lag that a separate RAF would introduce when the
  // two RAFs fire in the wrong order (tick sets camera → render event → icon).
  useEffect(() => {
    if (phase === "idle" || phase === "complete") return;

    const el     = carElRef.current;
    const rawMap = rawMapRef.current;
    if (!el || !rawMap) return;

    rawMap.getContainer().appendChild(el);

    function onRender() {
      const pos = carPositionRef.current;
      if (pos) {
        const px = rawMap.project([pos.lng, pos.lat]);
        el.style.transform =
          `translate(${px.x - 16}px,${px.y - 16}px) rotate(${pos.bearing}deg)`;
        el.style.display = "block";
      } else {
        el.style.display = "none";
      }
    }

    rawMap.on("render", onRender);
    // Kick off the first paint immediately so the icon isn't invisible
    // until the next natural render event.
    rawMap.triggerRepaint();

    return () => {
      rawMap.off("render", onRender);
      el.remove();
    };
  }, [phase, carPositionRef, rawMapRef]);

  // ── Derived state ─────────────────────────────────────────────────────────

  const isActive  = phase !== "idle" && phase !== "complete";
  const isDriving = phase === "driving";
  const isPaused  = phase === "paused";
  const isViewing = phase === "viewing";

  // The bar (stats, scene photos, controls — including Settings) is a map HUD:
  // it only belongs on screen while driving between photos. It stays out of
  // the way for the route overview and for every photo's viewing (or a pause
  // that started from viewing), not just the first one. "Arriving" is only
  // suppressed for the first photo, where nothing has driven yet.
  const isViewingPhoto = phase === "viewing" || (phase === "paused" && pausedFrom === "viewing");
  const inOpeningStage =
    phase === "overview" ||
    isViewingPhoto ||
    (targetWaypointIndex === 0 && phase === "arriving");
  const showBar = isActive && !inOpeningStage;

  const completedCount = Math.max(0, targetWaypointIndex - 1);
  const totalStops     = waypoints.length;

  // ── Styles ────────────────────────────────────────────────────────────────

  const glass ="bg-canvas/75 border-ink/10 text-ink";

  const btnBase ="text-tone-400 hover:text-ink hover:bg-ink/10 border-ink/10";

  function iconBtn(label: string, onClick: () => void, children: React.ReactNode, active = false) {
    return (
      <button
        title={label}
        onClick={onClick}
        className={`w-9 h-9 flex items-center justify-center rounded-xl border transition-all ${
          active ? "bg-indigo-600 border-indigo-500 text-white" : btnBase
        }`}
      >
        {children}
      </button>
    );
  }

  // Shared between the driving-phase control bar and the standalone paused
  // row below — reused so both stay visually identical. `currentIdx` differs
  // by context: while driving, completedCount (the last *departed* stop) is
  // "current" since the destination hasn't been reached yet; while paused on
  // a photo, targetWaypointIndex (the photo actually being viewed) is.
  function waypointPills(currentIdx = completedCount) {
    return waypoints.map((wp, i) => {
      const done    = i < currentIdx;
      const current = i === currentIdx && phase !== "complete";
      const thumb   = wp.thumbnailUrl;
      return (
        <div
          key={wp.panoId}
          title={wp.name}
          className={`w-7 h-7 rounded-lg overflow-hidden border-2 transition-all shrink-0 ${
            current
              ? "border-indigo-400 scale-110 shadow-lg shadow-indigo-500/30"
              : done
                ? "border-emerald-500/60 opacity-50"
                :"border-ink/10 opacity-40"
          }`}
        >
          {thumb
            ? <img src={thumb} alt="" className="w-full h-full object-cover" />
            : <div className="w-full h-full flex items-center justify-center bg-tone-700">
                <MapPin className="w-3 h-3 text-tone-400" />
              </div>
          }
        </div>
      );
    });
  }

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <AnimatePresence>
      {showBar && (
        <motion.div
          key="tour-player"
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 16 }}
          transition={{ type: "spring", stiffness: 350, damping: 28 }}
          className="fixed bottom-6 left-1/2 z-[160] -translate-x-1/2 flex flex-col items-center gap-2"
        >
          {/* Stats row — above control bar */}
          {routeStats.totalMeters > 0 && (
            <div className={`flex items-center gap-4 px-4 py-2 rounded-xl border shadow-lg backdrop-blur-2xl text-xs ${glass}`}>
              <div className="flex flex-col items-center gap-0.5">
                <span className={`text-[9px] font-black uppercase tracking-widest text-tone-500`}>Distance</span>
                <span className="font-semibold tabular-nums">
                  {fmtDist(routeStats.currentMeters)}
                  <span className={`ml-1 font-normal text-tone-500`}>/ {fmtDist(routeStats.totalMeters)}</span>
                </span>
              </div>
              <div className={`w-px h-6 shrink-0 bg-ink/10`} />
              <div className="flex flex-col items-center gap-0.5">
                <span className={`text-[9px] font-black uppercase tracking-widest text-tone-500`}>Drive Time</span>
                <span className="font-semibold tabular-nums">
                  {fmtTime(routeStats.elapsedSec)}
                  <span className={`ml-1 font-normal text-tone-500`}>/ {fmtTime(routeStats.totalSec)}</span>
                </span>
              </div>
            </div>
          )}

          {/* Auto-pause notice — only while the menu itself caused the pause */}
          <AnimatePresence>
            {settingsOpen && autoPausedByMenuRef.current && phase === "paused" && (
              <motion.div
                key="auto-pause-toast"
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 8 }}
                className="px-3 py-1.5 rounded-xl border shadow-lg backdrop-blur-2xl text-xs font-semibold bg-amber-500/15 border-amber-500/30 text-amber-300"
              >
                Tour paused — close settings to resume
              </motion.div>
            )}
          </AnimatePresence>

          {/* Settings panel — above control bar */}
          <AnimatePresence>
            {settingsOpen && (
              <TourRouteSettings
                key="tour-settings"
                settings={settings}
                onSettingsChange={setSettings}
                onClose={() => setSettingsOpen(false)}
                isDark={isDark}
              />
            )}
          </AnimatePresence>

          {/* Control bar */}
          <div className={`flex items-center gap-2 px-4 py-3 rounded-2xl border shadow-2xl backdrop-blur-2xl ${glass}`}>

            {/* Waypoint pills */}
            <div className="flex items-center gap-1.5 mr-1">
              {waypointPills()}
            </div>

            {/* Divider */}
            <div className={`w-px h-6 shrink-0 bg-ink/10`} />

            {/* Phase label */}
            <span className={`text-[10px] font-black uppercase tracking-widest w-16 text-center shrink-0 text-tone-500`}>
              {phase === "overview"  ? "Overview"
               : phase === "driving"  ? `Stop ${targetWaypointIndex}`
               : phase === "arriving" ? "Arriving"
               : phase === "viewing"  ? "Viewing"
               : phase === "paused"   ? "Paused"
               : ""}
            </span>

            {/* Divider */}
            <div className={`w-px h-6 shrink-0 bg-ink/10`} />

            {/* Controls */}
            {isPaused
              ? iconBtn("Resume", resume, <Play className="w-4 h-4" />)
              : iconBtn("Pause", pause, <Pause className="w-4 h-4" />, isDriving || isViewing)
            }
            {iconBtn("Skip to next", skip, <SkipForward className="w-4 h-4" />, false)}
            {iconBtn("Stop", stop, <Square className="w-3.5 h-3.5" />)}

            {/* Divider */}
            <div className={`w-px h-6 shrink-0 bg-ink/10`} />

            {iconBtn("Settings", () => setSettingsOpen((v) => !v), <Settings2 className="w-4 h-4" />, settingsOpen)}
          </div>

          {/* Progress bar */}
          {isDriving && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className={`w-full h-0.5 rounded-full overflow-hidden bg-ink/10`}
            >
              <div
                className="h-full bg-indigo-500 transition-none"
                style={{ width: `${segmentProgress * 100}%` }}
              />
            </motion.div>
          )}
        </motion.div>
      )}

      {/* ── Paused-while-viewing: pills only, no Settings/controls ─────────── */}
      {/* The full bar above stays hidden while viewing any photo, paused or
          not (inOpeningStage) — but once paused, the photo list should still
          be visible. This mirrors just the pills, not the rest of the bar. */}
      <AnimatePresence>
        {phase === "paused" && pausedFrom === "viewing" && (
          <motion.div
            key="paused-pills"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 16 }}
            transition={{ type: "spring", stiffness: 350, damping: 28 }}
            className="fixed bottom-6 left-1/2 z-[160] -translate-x-1/2"
          >
            <div className={`flex items-center gap-1.5 px-3 py-2 rounded-2xl border shadow-2xl backdrop-blur-2xl ${glass}`}>
              {waypointPills(targetWaypointIndex)}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Completion banner */}
      {phase === "complete" && (
        <motion.div
          key="tour-complete"
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          transition={{ type: "spring", stiffness: 350, damping: 28 }}
          className={`fixed top-1/2 left-1/2 z-[160] -translate-x-1/2 -translate-y-1/2 flex items-center gap-3 px-5 py-3 rounded-2xl border shadow-2xl backdrop-blur-2xl ${glass}`}
        >
          <span className="text-sm font-bold">Tour complete!</span>
          <span className={`text-xs text-tone-400`}>
            {totalStops} stop{totalStops !== 1 ? "s" : ""} visited
          </span>
          <button
            onClick={() => { stop(); onDismiss?.(); }}
            className={`ml-2 px-3 py-1.5 rounded-lg text-xs font-bold border transition-all ${btnBase}`}
          >
            Dismiss
          </button>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
